import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useCreateFileShare } from '@/hooks/useFileShares'
import { X } from 'lucide-react'

interface FileShareDialogProps {
	fileId: string
	onClose: () => void
}

export function FileShareDialog({ fileId, onClose }: FileShareDialogProps) {
	const [userId, setUserId] = useState('')
	const [permission, setPermission] = useState('download')
	const createShareMutation = useCreateFileShare()

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!userId.trim()) return

		await createShareMutation.mutateAsync({
			fileId,
			data: { userId: userId.trim(), permission },
		})
		onClose()
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="share-dialog">
			<div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
				<div className="flex items-center justify-between mb-4">
					<h3 className="text-lg font-semibold">Share File</h3>
					<button type="button" onClick={onClose} aria-label="Close">
						<X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
					</button>
				</div>

				<form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
					<div>
						<label htmlFor="share-user-id" className="block text-sm font-medium text-gray-700 mb-1">
							User ID
						</label>
						<Input
							id="share-user-id"
							placeholder="Enter user ID to share with"
							value={userId}
							onChange={(e) => setUserId(e.target.value)}
						/>
					</div>

					<div>
						<label htmlFor="share-permission" className="block text-sm font-medium text-gray-700 mb-1">
							Permission
						</label>
						<select
							id="share-permission"
							value={permission}
							onChange={(e) => setPermission(e.target.value)}
							className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
						>
							<option value="view">View (metadata only)</option>
							<option value="download">Download (metadata + file)</option>
						</select>
					</div>

					<div className="flex justify-end gap-2 pt-2">
						<Button variant="outline" type="button" onClick={onClose}>
							Cancel
						</Button>
						<Button type="submit" disabled={!userId.trim() || createShareMutation.isPending}>
							{createShareMutation.isPending ? 'Sharing...' : 'Share'}
						</Button>
					</div>
				</form>
			</div>
		</div>
	)
}
